import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface PhoneInputProps {
    value: string; // The full normalized phone number (e.g., +15551234567)
    onChange: (value: string) => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    className?: string;
    onBlur?: () => void;
    label?: string;
    autoFocus?: boolean;
}

const UsFlagIcon: React.FC = () => (
    <div className="w-5.5 h-4 flex items-center justify-center shrink-0" aria-hidden="true">
        <svg width="22" height="16" viewBox="0 0 22 16" fill="none" className="rounded-[3px] overflow-hidden shadow-xs">
            <rect width="22" height="16" fill="#FFFFFF" />
            <rect y="0" width="22" height="1.23" fill="#DC2626" />
            <rect y="2.46" width="22" height="1.23" fill="#DC2626" />
            <rect y="4.92" width="22" height="1.23" fill="#DC2626" />
            <rect y="7.38" width="22" height="1.23" fill="#DC2626" />
            <rect y="9.84" width="22" height="1.23" fill="#DC2626" />
            <rect y="12.3" width="22" height="1.23" fill="#DC2626" />
            <rect y="14.76" width="22" height="1.24" fill="#DC2626" />
            <rect width="9.6" height="8.6" fill="#1D4ED8" />
            <circle cx="2.1" cy="2.1" r="0.7" fill="#FFFFFF" />
            <circle cx="4.8" cy="2.1" r="0.7" fill="#FFFFFF" />
            <circle cx="7.5" cy="2.1" r="0.7" fill="#FFFFFF" />
            <circle cx="3.45" cy="4.3" r="0.7" fill="#FFFFFF" />
            <circle cx="6.15" cy="4.3" r="0.7" fill="#FFFFFF" />
            <circle cx="2.1" cy="6.5" r="0.7" fill="#FFFFFF" />
            <circle cx="4.8" cy="6.5" r="0.7" fill="#FFFFFF" />
            <circle cx="7.5" cy="6.5" r="0.7" fill="#FFFFFF" />
        </svg>
    </div>
);

const PhoneInput: React.FC<PhoneInputProps> = ({
    value,
    onChange,
    placeholder = "(555) 000-0000",
    required = false,
    disabled = false,
    className = "",
    onBlur,
    label = "Phone number",
    autoFocus = false,
}) => {
    const [localNumber, setLocalNumber] = useState('');
    const inputRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
        if (value.startsWith('+1')) {
            setLocalNumber(value.substring(2).replace(/[^\d]/g, '').slice(0, 10));
        } else if (/^\d{11}$/.test(value) && value.startsWith('1')) {
            // Handle legacy stored values like "15551234567" (no plus sign).
            setLocalNumber(value.substring(1).slice(0, 10));
        } else {
            // Default to +1 if no prefix
            setLocalNumber(value.replace(/[^\d]/g, '').slice(0, 10));
        }
    }, [value]);

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    const handleLocalNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const raw = e.target.value.replace(/[^\d]/g, '');
        const normalizedLocal = (raw.length === 11 && raw.startsWith('1') ? raw.slice(1) : raw).slice(0, 10);
        setLocalNumber(normalizedLocal);
        onChange(`+1${normalizedLocal}`);
    };

    const handleClear = () => {
        setLocalNumber('');
        onChange('');
        inputRef.current?.focus();
    };

    // Simple formatting for display: (XXX) XXX-XXXX
    const formatDisplay = (num: string) => {
        if (num.length <= 3) return num;
        if (num.length <= 6) return `(${num.slice(0, 3)}) ${num.slice(3)}`;
        return `(${num.slice(0, 3)}) ${num.slice(3, 6)}-${num.slice(6, 10)}`;
    };

    return (
        <div className={`space-y-1.5 text-left ${className}`}>
            {label && (
                <label className="text-xs font-bold text-slate-500 block tracking-tight">
                    {label}
                </label>
            )}
            <div className="flex items-center gap-3 bg-[#F8FAFC] border border-slate-200/80 rounded-2xl px-4 py-3.5 focus-within:bg-white focus-within:border-teal-500 focus-within:ring-4 focus-within:ring-teal-500/10 transition-all duration-200 shadow-xs">
                <div className="flex items-center gap-2 select-none shrink-0">
                    <UsFlagIcon />
                    <span className="text-sm font-black text-slate-900 tracking-tight">+1</span>
                </div>
                <div className="h-5 w-px bg-slate-200 shrink-0" />
                <input
                    ref={inputRef}
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    required={required}
                    disabled={disabled}
                    value={formatDisplay(localNumber)}
                    onChange={handleLocalNumberChange}
                    onBlur={onBlur}
                    placeholder={placeholder}
                    className="w-full bg-transparent border-0 p-0 text-base font-bold text-slate-900 placeholder:text-slate-400 placeholder:font-medium focus:outline-none focus:ring-0 focus:border-transparent tracking-tight"
                />
                {localNumber.length > 0 && !disabled && (
                    <button
                        type="button"
                        onClick={handleClear}
                        className="p-1 text-slate-400 hover:text-slate-600 rounded-full hover:bg-slate-100 transition-colors shrink-0"
                        aria-label="Clear phone number"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>
        </div>
    );
};

export default PhoneInput;
